import React, { useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import { useQuery } from "@apollo/client"
import { SplitButton } from "primereact/splitbutton"
import { parseDate } from "../../helpers/dateNormalization"
import { Calendar } from "primereact/calendar"
import { SUPERVISION_SESSION } from "./queries"
import { UserContext } from "../../context/userContext"
import dataParser from "../../helpers/parseJsonData"
import { LoaderMedium } from "../../components/Loaders"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import useLabels from "../../hooks/useLabels"
import { Button } from "primereact/button"
import { MultiSelect } from "primereact/multiselect"
import { FilterService } from "primereact/api"
import { navigateWithOrigin } from "../../helpers/navigation"
import {
  isOrganizationAdmin,
  isOrganizationSupervisor,
} from "../../helpers/staffRoleHelpers"

const formatDate = (value) => {
  if (!value) return "Invalid Date"
  const date = new Date(value)
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

export default function SupervisionSessions() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [dateRange, setDateRange] = useState(null)
  const [loadingLabels, labels] = useLabels(["individual_supervision_session"])
  const userRole = userCtx.session.user?.staff_role
  const userStaffId = userCtx.session.user?.staff_id
  const changeCurrentStaffMembershipId = userCtx.changeCurrentStaffMembershipId
  const changeParticipant = userCtx.changeParticipant
  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`
  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
    onMultiSelectFilterChange,
  } = useFilters()

  useEffect(() => {
    FilterService.register("arrayContains", (value, filter) => {
      if (!value || !Array.isArray(value)) return false
      return value.some((item) => filter.includes(item))
    })
  }, [])

  const { loading } = useQuery(SUPERVISION_SESSION, {
    variables: {
      organization_id: userCtx.session.user?.organization_id,
      staff_id: userCtx.session.user?.staff_id,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.supervisionSessions === null) {
        setSessions([])
        return setLoadingPage(false)
      }
      const formattedData = dataParser(data.supervisionSessions.data)
      const groupedByStaff = _.groupBy(
        formattedData,
        "supervision__staff_membership_id",
      )

      Object.keys(groupedByStaff).forEach((staffId) => {
        const staffSessions = groupedByStaff[staffId]

        const oldestSession = _.minBy(
          staffSessions,
          (session) => new Date(session.session_date),
        )

        if (oldestSession) {
          oldestSession.plan_changed = "Yes"
        }
      })

      setSessions(formattedData)
      setLoadingPage(false)
    },
  })

  const formattedSessions = useMemo(
    () =>
      sessions.map((row) => {
        const programs = _.map(row.program_ids, "label").join(", ")
        const locationLabels = _.map(row.location_ids, "label").join(", ")
        const isFacilitator = row.staff_id === userStaffId

        let canEdit = false
        if (!isOrganizationSupervisor(userRole, true)) {
          // Anything below org supervisor role cannot edit
          canEdit = false
        } else if (isOrganizationSupervisor(userRole) && isFacilitator) {
          // Org supervisors can edit if they are the facilitator for the session
          canEdit = true
        } else if (isOrganizationAdmin(userRole, true)) {
          // Org admins and above can edit all sessions
          canEdit = true
        }

        const actionButton = canEdit ? (
          <SplitButton
            label="View"
            onClick={async () => {
              await changeCurrentStaffMembershipId(
                row?.supervision__staff_membership_id,
              )
              changeParticipant(undefined)
              navigateWithOrigin(
                navigate,
                `/form/view/individual_supervision_session/${row.idStamp}`,
              )
            }}
            model={[
              {
                label: "Edit",
                command: async () => {
                  await changeCurrentStaffMembershipId(
                    row?.supervision__staff_membership_id,
                  )
                  navigate(
                    `/form/individual_supervision_session/1/${row.idStamp}`,
                  )
                },
              },
            ]}
          />
        ) : (
          <Button
            style={{ minWidth: "100px" }}
            label="View"
            onClick={async () => {
              await changeCurrentStaffMembershipId(
                row?.supervision__staff_membership_id,
              )
              changeParticipant(undefined)
              navigateWithOrigin(
                navigate,
                `/form/view/individual_supervision_session/${row.idStamp}`,
              )
            }}
          />
        )

        return {
          ...row,
          date: parseDate(row.session_date),
          length: `${row.session_length} minutes`,
          staff_member: row.staff_member || "",
          staff_name: row.supervision_plan_staff || "",
          facilitating_staff: row.staff_full_name || "",
          supervision_session_topic: row.supervision_session_topic || [],
          plan_change: row.plan_changed ? row.plan_changed : "No",
          programs: programs || "",
          location_labels: locationLabels || "",
          edit: actionButton,
        }
      }),
    [
      navigate,
      sessions,
      changeCurrentStaffMembershipId,
      changeParticipant,
      userRole,
      userStaffId,
    ],
  )

  const filterConfig = useMemo(
    () => [
      {
        label: "Date",
        value: dateRange,
        options: null,
        onChange: (e) => {
          setDateRange(e.value)
          onFilterChange("date", e.value)
        },
        onClear: () => {
          setDateRange(null)
          onFilterChange("date", null)
        },
        enableFilter: true,
        component: (
          <Calendar
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.value)
              onFilterChange("date", e.value)
            }}
            placeholder="Select Date Range"
            selectionMode="range"
            dateFormat="mm/dd/yy"
            readOnlyInput
            numberOfMonths={2}
            showIcon
            showButtonBar
            yearNavigator
            yearRange={yearRange}
          />
        ),
      },
      {
        label: "Locations",
        value: filters.location_labels?.value || "",
        options: [
          ...new Set(
            sessions.flatMap((session) =>
              Array.isArray(session.location_ids)
                ? session.location_ids.map((loc) => loc.label)
                : [],
            ),
          ),
        ]
          .map((location) => ({ label: location, value: location }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        onChange: (e) => onFilterChange("location_labels", e.value),
        onClear: () => onFilterChange("location_labels", null),
        placeholder: "Select Locations",
      },
      {
        label: "Programs",
        value: filters.programs?.value || "",
        options: [
          ...new Set(
            sessions.flatMap((session) =>
              Array.isArray(session.program_ids)
                ? session.program_ids.map((prog) => prog.label)
                : [],
            ),
          ),
        ]
          .map((program) => ({ label: program, value: program }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        onChange: (e) => onFilterChange("programs", e.value),
        onClear: () => onFilterChange("programs", null),
        placeholder: "Select Programs",
      },
      {
        label: "Facilitating Staff",
        value: filters.facilitating_staff?.value || "",
        options: [
          ...new Set(sessions.map((session) => session.staff_full_name)),
        ]
          .map((submitter) => ({ label: submitter, value: submitter }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        onChange: (e) => onFilterChange("facilitating_staff", e.value),
        onClear: () => onFilterChange("facilitating_staff", null),
        placeholder: "Select Facilitating Staff",
      },
      {
        label: "Staff Member",
        value: filters.staff_member?.value || "",
        options: [
          ...new Set(
            sessions.map((session) => session.staff_member).filter(Boolean),
          ),
        ]
          .map((staff) => ({ label: staff, value: staff }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        onChange: (e) => onFilterChange("staff_member", e.value),
        onClear: () => onFilterChange("staff_member", null),
        placeholder: "Select Staff Member",
      },
      {
        label: "Topics",
        placeholder: "Select Topics",
        component: (
          <MultiSelect
            value={filters.supervision_session_topic?.value || []}
            options={[
              ...new Set(
                sessions.flatMap(
                  (session) => session.supervision_session_topic || [],
                ),
              ),
            ].sort()}
            onChange={(e) =>
              onMultiSelectFilterChange("supervision_session_topic", e.value)
            }
            placeholder="Select Topics"
            display="chip"
            style={{
              maxWidth: "200px",
              overflowY: "auto",
            }}
          />
        ),
      },
    ],
    [
      dateRange,
      filters,
      onFilterChange,
      onMultiSelectFilterChange,
      sessions,
      yearRange,
    ],
  )

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  const columns = useMemo(
    () => [
      {
        field: "date",
        header: "Date",
        sortable: true,
        filter: true,
        body: (rowData) => formatDate(rowData.date),
      },
      {
        field: "staff_member",
        header: "Staff Member",
        sortable: true,
        filter: true,
      },
      {
        field: "facilitating_staff",
        header: "Facilitating Staff",
        sortable: true,
        filter: true,
      },
      {
        field: "location_labels",
        header: "Locations",
        sortable: true,
        filter: true,
      },
      {
        field: "programs",
        header: "Program ID",
        sortable: true,
        filter: true,
      },
      {
        field: "supervision_session_topic",
        header: "Topics",
        sortable: true,
        filter: true,
        body: (rowData) => {
          return rowData.supervision_session_topic.join(", ")
        },
      },
      {
        field: "plan_change",
        header: "Plan Changed",
        sortable: true,
        filter: true,
      },

      { field: "edit", header: "Actions" },
    ],
    [],
  )

  if (loadingPage || loading || loadingLabels) return <LoaderMedium />

  return (
    <div className="layout-full">
      <div className="main-content">
        <h1>{labels.individual_supervision_session}</h1>

        <div
          className="mb-5 p-tag p-tag-warning"
          style={{ backgroundColor: "var(--yellow-200)" }}
        >
          <p>
            Supervision Sessions can be initiated from the{" "}
            <a
              style={{ textDecoration: "underline" }}
              href="/organization/staff"
            >
              Staff Member profile
            </a>
          </p>
        </div>
        <br />
        <br />
        <GenericTable
          data={formattedSessions}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={handleClearAllFilters}
            />
          }
          paginator
          rows={50}
        />
      </div>
    </div>
  )
}
